import React, { useState } from 'react';
import { Alert, Button, Empty, Space, Spin, Table, Typography } from 'antd';
import { useApiRequest } from '../../hooks/useApiRequest';
import { useResource } from '../../hooks/useResource';
import { userMessageForError } from '../../utils/apiError';
import { formatDdMmYyyyTimeIst } from '../../utils/formatDdMmYyyy';
import { ADMIN_API, AUDIT_PAGE, normaliseAuditEntries } from '../../utils/regulatoryAdminModel';

const { Text } = Typography;

/** Audit trail for one document (Commandment VI), newest first: IST date, action, outcome, actor, resource and details. */
const RegulationAuditTrail = ({ id }) => {
    const { request } = useApiRequest();
    const [offset, setOffset] = useState(0);
    const res = useResource(async () => {
        const { data, meta } = await request('GET', ADMIN_API.audit(id), { query: { limit: AUDIT_PAGE, offset } });
        return { entries: normaliseAuditEntries(data), mongoAvailable: data?.mongoAvailable !== false, total: meta?.total ?? 0 };
    }, [request, id, offset]);

    if (res.status === 'loading' && !res.data) return <Spin />;
    if (res.status === 'error') {
        return <Alert type="error" message={userMessageForError(res.error)} action={<Button onClick={res.reload}>Retry</Button>} />;
    }
    const { entries, mongoAvailable, total } = res.data;
    if (!mongoAvailable) return <Alert type="warning" data-testid="audit-unavailable" message="The audit store is unavailable right now, so the trail cannot be shown. Nothing has been lost; try again shortly." action={<Button onClick={res.reload}>Retry</Button>} />;
    if (entries.length === 0) return <Empty description="No audit entries recorded for this document." />;
    const columns = [
        { title: 'Date (IST)', key: 'ts', render: (_v, r) => formatDdMmYyyyTimeIst(r.ts) },
        { title: 'Action', key: 'action', render: (_v, r) => r.action },
        { title: 'Outcome', key: 'outcome', render: (_v, r) => r.outcome || '-' },
        { title: 'Actor', key: 'actor', render: (_v, r) => r.actor || '-' },
        { title: 'Resource', key: 'resource', render: (_v, r) => r.resource || '-' },
        { title: 'Details', key: 'details', render: (_v, r) => <Text type="secondary">{r.details || '-'}</Text> },
    ];
    return (
        <div>
            <Table columns={columns} dataSource={entries} rowKey={(r) => r.key} pagination={false} size="small" />
            {total > AUDIT_PAGE && (
                <Space style={{ marginTop: 8 }}>
                    <Button disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - AUDIT_PAGE))}>Newer entries</Button>
                    <Text type="secondary">{offset + 1}-{offset + entries.length} of {total}</Text>
                    <Button disabled={offset + entries.length >= total} onClick={() => setOffset(offset + AUDIT_PAGE)}>Older entries</Button>
                </Space>
            )}
        </div>
    );
};

export default RegulationAuditTrail;
